import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  MessageSquare,
  BarChart3,
  HelpCircle,
  Settings,
  Activity,
  Mail,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Shield,
  User,
  KeyRound
} from 'lucide-react';
import { adminAuthService } from '../services/adminAuthService';
import { usePermissions } from '../contexts/PermissionsContext';

// NAV_ITEMS structure with permissions - defined outside component to prevent recreation on every render
const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/admin',
    icon: LayoutDashboard,
    permission: 'dashboard.view'
  },
  {
    label: 'Platform Users',
    path: '/admin/users',
    icon: Shield,
    permission: 'platform_users.view'
  },
  {
    label: 'Companies',
    icon: Building2,
    permission: 'companies.view',
    children: [
      { label: 'All Companies', path: '/admin/companies', permission: 'companies.view' },
      { label: 'Company Users', path: '/admin/companies/users', permission: 'company_users.view' },
    ]
  },
  {
    label: 'Subscriptions',
    icon: CreditCard,
    permission: 'subscriptions.view',
    children: [
      { label: 'Subscriptions', path: '/admin/subscriptions', permission: 'subscriptions.view' },
      { label: 'Plans', path: '/admin/subscriptions/plans', permission: 'plans.view' },
      { label: 'Invoices', path: '/admin/subscriptions/invoices', permission: 'invoices.view' },
    ]
  },
  {
    label: 'Chatbots',
    path: '/admin/chatbots',
    icon: MessageSquare,
    permission: 'chatbots.view'
  },
  {
    label: 'Analytics',
    path: '/admin/analytics',
    icon: BarChart3,
    permission: 'analytics.view'
  },
  {
    label: 'Support',
    icon: HelpCircle,
    permission: 'support.view',
    children: [
      { label: 'Tickets', path: '/admin/support/tickets', permission: 'support.view' },
    ]
  },
  {
    label: 'System',
    icon: Settings,
    permission: null,
    children: [
      { label: 'Settings', path: '/admin/system/settings', permission: 'system.settings' },
      { label: 'API Keys', path: '/admin/system/api-keys', permission: 'system.api_keys' },
      { label: 'Roles & Permissions', path: '/admin/system/roles', permission: 'roles.view' },
    ]
  },
  {
    label: 'Activity Log',
    path: '/admin/activity',
    icon: Activity,
    permission: 'activity.view'
  },
  {
    label: 'Newsletter',
    path: '/admin/newsletter',
    icon: Mail,
    permission: null
  },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [adminUser, setAdminUser] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef(null);

  // Get permissions from context
  const permissions = usePermissions();

  // Helper function to check permissions
  const hasPermission = (key) => {
    if (!key) return true; // null permission = always visible
    return permissions.includes(key) || permissions.includes('*');
  };

  useEffect(() => {
    // Get admin user data
    const user = adminAuthService.getAdminUser();
    setAdminUser(user);
  }, []);

  // Filter navigation items based on permissions (memoized to avoid re-creating on every render)
  const visibleItems = useMemo(() => {
    return NAV_ITEMS.filter(item => {
      if (item.children) {
        return item.children.some(child => hasPermission(child.permission));
      }
      return hasPermission(item.permission);
    }).map(item => ({
      ...item,
      children: item.children?.filter(child => hasPermission(child.permission))
    }));
  }, [permissions]);

  // Initialize open menus based on current route
  useEffect(() => {
    const newOpenMenus = {};
    visibleItems.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child =>
          location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (isChildActive) {
          newOpenMenus[item.label] = true;
        }
      }
    });
    setOpenMenus(newOpenMenus);
  }, [location.pathname, visibleItems]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [userMenuOpen]);

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    adminAuthService.logout();
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  // Fixed: Only exact match should be highlighted for child items
  const isChildActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'w-64' : 'w-0'
      } bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-all duration-300 ease-in-out flex flex-col overflow-hidden`}>
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg">Admin Portal</span>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {visibleItems.map((item) => (
            <div key={item.label} className="mb-1">
              {item.children ? (
                // Menu with children (accordion)
                <div>
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-700/50 transition-colors ${
                      openMenus[item.label] ? 'bg-gray-700/50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        openMenus[item.label] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Submenu - FIXED: Only exact match gets highlighted */}
                  {openMenus[item.label] && (
                    <div className="mt-1 ml-4 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                            isChildActive(child.path)
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                          }`}
                        >
                          <span className="text-sm">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Single menu item
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User Section - FIXED: Click outside to close */}
        <div className="border-t border-gray-700 p-3">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold">
                  {adminUser?.firstName?.charAt(0)}{adminUser?.lastName?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-white">
                  {adminUser?.firstName} {adminUser?.lastName}
                </p>
                <p className="text-xs text-gray-400">{adminUser?.platformRole}</p>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${
                userMenuOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
                <Link
                  to="/admin/profile"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition-colors text-gray-200 hover:text-white"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">My Profile</span>
                </Link>
                <Link
                  to="/admin/profile?tab=security"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition-colors text-gray-200 hover:text-white"
                >
                  <KeyRound className="w-4 h-4" />
                  <span className="text-sm font-medium">Change Password</span>
                </Link>
                <div className="border-t border-gray-700" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition-colors text-red-400 hover:text-red-300"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Full Width */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
          {/* Left side - Menu toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {visibleItems.find(item => isActive(item.path) && !item.children)?.label ||
                 visibleItems.find(item => item.children?.some(child => isChildActive(child.path)))?.label ||
                 'Admin Dashboard'}
              </h1>
            </div>
          </div>

          {/* Right side - User info */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                {adminUser?.firstName} {adminUser?.lastName}
              </p>
              <p className="text-xs text-gray-500">
                {adminUser?.platformRole === 'PLATFORM_OWNER' ? 'Platform Owner' : 'Platform Admin'}
              </p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-blue-600 rounded-full flex items-center justify-center shadow-md">
              <span className="text-sm font-bold text-white">
                {adminUser?.firstName?.charAt(0)}{adminUser?.lastName?.charAt(0)}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content - Full Width */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Sign Out</h2>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to sign out of the admin portal?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
